/**
 * Insert Option Chain
 * Inserts option chain into Excel with entity-first layout and R1C1 formulas
 */

/* global Excel */

import { runExcel } from "../runExcel";
import { logger } from "../../shared/logger";
import { UserInputError } from "../../shared/errors/UserInputError";
import { ChainContract } from "./buildOptionChain";
import { getOptionPropertyName } from "../entities/optionEntity";
import {
  buildEntityPropertyRefR1C1,
  buildNumericFormula,
  buildTextFormula,
  buildDateTimeFormula,
} from "./formulas";
import { ZERO_DEFAULT_FIELDS, BLANK_DEFAULT_FIELDS } from "./chainFields";

export const BATCH_SIZE = 20;

export interface InsertChainInput {
  contracts: ChainContract[];
  fieldKeys: string[];
  mode: "spill" | "table";
  onProgress?: (done: number, total: number) => void;
}

export interface ChainGrid {
  headerValues: string[][];
  bodyFormulasR1C1: string[][];
}

/**
 * Determine field type for formula generation
 * @param fieldKey - Field key from schema
 * @returns Field type: "number", "datetime", or "text"
 */
function getFieldType(fieldKey: string): "number" | "datetime" | "text" {
  // Datetime fields
  if (fieldKey === "updatedAt" || fieldKey === "updated_at") {
    return "datetime";
  }
  
  // All other fields in the schema are either numeric or text
  // Most are numeric (prices, greeks, counts), but some are text (underlying, right, expiration, display_name, occ_symbol)
  const textFields = new Set<string>([
    "underlying",
    "right",
    "expiration",
    "display_name",
    "occ_symbol",
  ]);
  
  if (textFields.has(fieldKey)) {
    return "text";
  }
  
  // Default to number for all other fields
  return "number";
}

/**
 * Pure helper: Build chain grid (header + body formulas)
 * Exposed for testing
 * @param contracts - Chain contracts to insert
 * @param fieldKeys - Field keys to include
 * @returns Grid with header values and body formulas (R1C1)
 */
export function buildChainGrids(contracts: ChainContract[], fieldKeys: string[]): ChainGrid {
  // Build header row
  const headerRow: string[] = ["Option"];
  for (const fieldKey of fieldKeys) {
    const propertyName = getOptionPropertyName(fieldKey);
    headerRow.push(propertyName);
  }
  const headerValues: string[][] = [headerRow];
  
  // Build body formulas (R1C1)
  const bodyFormulasR1C1: string[][] = [];
  
  for (const contract of contracts) {
    const formulaRow: string[] = [];
    
    // Column 0: OPTION formula (entity column)
    formulaRow.push("=OPTLAB.OPTIONS.OPTION(\"" + contract.occSymbol + "\")");
    
    // Columns 1..n: Property references using R1C1 relative to this table
    // Entity column is always the first column of the inserted range; from column j it is j columns left = RC[-j]
    for (let j = 0; j < fieldKeys.length; j++) {
      const fieldKey = fieldKeys[j];
      const propertyName = getOptionPropertyName(fieldKey);
      const entityRefR1C1 = "RC[" + -(j + 1) + "]"; // entity is (j+1) columns to the left
      const propRef = buildEntityPropertyRefR1C1(entityRefR1C1, propertyName);
      const fieldType = getFieldType(fieldKey);
      
      let formula: string;
      if (fieldType === "number") {
        if (ZERO_DEFAULT_FIELDS.has(fieldKey)) {
          formula = buildNumericFormula(entityRefR1C1, propRef, "zero");
        } else if (BLANK_DEFAULT_FIELDS.has(fieldKey)) {
          formula = buildNumericFormula(entityRefR1C1, propRef, "blank");
        } else {
          formula = buildNumericFormula(entityRefR1C1, propRef, "blank");
        }
      } else if (fieldType === "datetime") {
        formula = buildDateTimeFormula(entityRefR1C1, propRef);
      } else {
        formula = buildTextFormula(entityRefR1C1, propRef);
      }
      
      formulaRow.push(formula);
    }
    
    bodyFormulasR1C1.push(formulaRow);
  }
  
  return {
    headerValues,
    bodyFormulasR1C1,
  };
}

/**
 * Check if a value is empty (for overwrite protection)
 * @param value - Value to check
 * @returns true if empty
 */
function isEmptyValue(value: unknown): boolean {
  return value === "" || value == null || value === undefined;
}

/**
 * Insert option chain into Excel at selected cell
 * @param input - Insert input parameters
 * @throws UserInputError if overwrite detected or other validation fails
 */
export async function insertOptionChain(input: InsertChainInput): Promise<void> {
  const { contracts, fieldKeys, mode, onProgress } = input;
  
  if (contracts.length === 0) {
    throw new UserInputError("EMPTY_CHAIN", "No contracts to insert", "contracts");
  }
  
  if (fieldKeys.length === 0) {
    throw new UserInputError("EMPTY_FIELDS", "No fields selected", "fieldKeys");
  }
  
  await runExcel(async (context) => {
    // Get target range from selection
    const selection = context.workbook.getSelectedRange();
    selection.load(["rowIndex", "columnIndex", "rowCount", "columnCount"]);
    await context.sync();
    
    const startRow = selection.rowIndex; // 0-based
    const startCol = selection.columnIndex; // 0-based
    
    // Compute dimensions
    const totalRows = 1 + contracts.length; // 1 header + N contracts
    const totalCols = 1 + fieldKeys.length; // 1 entity column + N field columns
    
    const sheet = selection.worksheet;
    
    // Get target range
    const targetRange = sheet.getRangeByIndexes(startRow, startCol, totalRows, totalCols);
    
    // Preflight overwrite check
    targetRange.load(["values", "formulas"]);
    await context.sync();
    
    // Check values and formulas for non-empty content
    const values = targetRange.values as (string | number | boolean | null)[][];
    const formulas = targetRange.formulas as (string | number)[][];
    
    for (let r = 0; r < totalRows; r++) {
      for (let c = 0; c < totalCols; c++) {
        const value = values[r]?.[c];
        const formula = formulas[r]?.[c];
        
        // Treat as empty if value === "" || value == null || value === undefined
        const hasValue = !isEmptyValue(value);
        const hasFormula = !isEmptyValue(formula);
        
        if (hasValue || hasFormula) {
          throw new UserInputError(
            "OVERWRITE_DETECTED",
            "Target range contains data. Please select an empty cell or range.",
            "selection"
          );
        }
      }
    }
    
    // Best-effort: try to check valuesAsJson if available (for entity cells)
    try {
      const valuesAsJson = (targetRange as any).valuesAsJson;
      if (valuesAsJson !== undefined) {
        await context.sync();
        const jsonValues = valuesAsJson as any[][];
        for (let r = 0; r < totalRows; r++) {
          for (let c = 0; c < totalCols; c++) {
            const jsonValue = jsonValues[r]?.[c];
            if (!isEmptyValue(jsonValue)) {
              throw new UserInputError(
                "OVERWRITE_DETECTED",
                "Target range contains data. Please select an empty cell or range.",
                "selection"
              );
            }
          }
        }
      }
    } catch (e) {
      // valuesAsJson may not be available in all Excel versions - ignore errors
      logger.log("insertOptionChain", "valuesAsJson check skipped", e instanceof Error ? e.message : String(e));
    }
    
    // Build grids
    const grids = buildChainGrids(contracts, fieldKeys);
    
    // Write header
    const headerRange = sheet.getRangeByIndexes(startRow, startCol, 1, totalCols);
    headerRange.values = grids.headerValues;
    
    // Format header if table mode
    if (mode === "table") {
      headerRange.format.font.bold = true;
    }
    
    await context.sync();
    
    // Write body formulas in batches
    const bodyStartRow = startRow + 1;
    let done = 0;
    const total = contracts.length;
    
    for (let batchStart = 0; batchStart < total; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, total);
      const batchSize = batchEnd - batchStart;
      
      const batchRange = sheet.getRangeByIndexes(
        bodyStartRow + batchStart,
        startCol,
        batchSize,
        totalCols
      );
      
      const batchFormulas = grids.bodyFormulasR1C1.slice(batchStart, batchEnd);
      batchRange.formulasR1C1 = batchFormulas;
      
      await context.sync();
      
      done = batchEnd;
      if (onProgress) {
        onProgress(done, total);
      }
    }
    
    // Formatting for table mode (after all writes)
    if (mode === "table") {
      const fullRange = sheet.getRangeByIndexes(startRow, startCol, totalRows, totalCols);
      
      // Borders (best-effort)
      try {
        fullRange.format.borders.getItem("EdgeTop").style = "Continuous";
        fullRange.format.borders.getItem("EdgeBottom").style = "Continuous";
        fullRange.format.borders.getItem("EdgeLeft").style = "Continuous";
        fullRange.format.borders.getItem("EdgeRight").style = "Continuous";
        fullRange.format.borders.getItem("InsideHorizontal").style = "Continuous";
        fullRange.format.borders.getItem("InsideVertical").style = "Continuous";
      } catch (e) {
        // Borders may fail in some contexts - ignore
        logger.log("insertOptionChain", "border formatting skipped", e instanceof Error ? e.message : String(e));
      }
      
      // Autofit columns (best-effort)
      try {
        fullRange.format.autofitColumns();
      } catch (e) {
        // Autofit may fail in some contexts - ignore
        logger.log("insertOptionChain", "autofit skipped", e instanceof Error ? e.message : String(e));
      }
      
      await context.sync();
    }
    
    logger.log("insertOptionChain", "inserted chain", "rows=" + totalRows + " cols=" + totalCols + " contracts=" + contracts.length);
  });
}
